import { prepareWithSegments, layoutWithLines, measureLineStats } from '@chenglou/pretext';

/**
 * Card-style node shared by the 2D and 3D graphs: a rounded rectangle with the
 * wrapped label and the type underneath (ported from Olisé).
 */

const LABEL_PX = 13;
const TYPE_PX = 10;
const LINE_H = 16;
const MAX_W = 150;
const PAD_X = 12;
const PAD_Y = 9;
const GAP = 4;
const FONT_STACK = 'system-ui, -apple-system, "Segoe UI", sans-serif';
const LABEL_FONT = `600 ${LABEL_PX}px ${FONT_STACK}`;
const TYPE_FONT = `500 ${TYPE_PX}px ${FONT_STACK}`;
export const SELECTED_COLOR = '#FF1910';

export interface CardLayout {
  lines: string[];
  w: number;
  h: number;
}

const cache = new Map<string, CardLayout>();

export function measureCard(label: string, type: string): CardLayout {
  const key = `${label}\u0000${type}`;
  const hit = cache.get(key);
  if (hit) return hit;

  const prepared = prepareWithSegments(label || '?', LABEL_FONT, { whiteSpace: 'pre-wrap' });
  const result = layoutWithLines(prepared, MAX_W, LINE_H);
  const lines = result.lines.map((l) => l.text).slice(0, 3);
  const labelW = Math.max(0, ...result.lines.map((l) => l.width));
  const typeW = measureLineStats(prepareWithSegments(type || '', TYPE_FONT), 10000).maxLineWidth;

  const innerW = Math.max(labelW, typeW, 40);
  const layout: CardLayout = {
    lines,
    w: Math.min(innerW, MAX_W) + PAD_X * 2,
    h: lines.length * LINE_H + GAP + TYPE_PX + PAD_Y * 2,
  };
  cache.set(key, layout);
  return layout;
}

/** Black or white text depending on the fill's luminance. */
export function contrastText(hex: string): string {
  const m = hex.replace('#', '');
  const r = parseInt(m.slice(0, 2), 16) / 255;
  const g = parseInt(m.slice(2, 4), 16) / 255;
  const b = parseInt(m.slice(4, 6), 16) / 255;
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.6 ? '#0A0A0B' : '#FFFFFF';
}

function roundRect(ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, r: number) {
  const rr = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

interface DrawOpts {
  color: string;
  type: string;
  selected?: boolean;
  borderColor: string;
}

/** Draw the card centred at (cx, cy), sized in px; the caller handles scaling. */
export function drawCardAt(ctx: CanvasRenderingContext2D, cx: number, cy: number, card: CardLayout, opts: DrawOpts) {
  const { w, h } = card;
  ctx.save();
  roundRect(ctx, cx - w / 2, cy - h / 2, w, h, 8);
  ctx.fillStyle = opts.color;
  ctx.fill();
  ctx.lineWidth = opts.selected ? 3 : 1;
  ctx.strokeStyle = opts.selected ? SELECTED_COLOR : opts.borderColor;
  ctx.stroke();

  const fg = contrastText(opts.color);
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.font = LABEL_FONT;
  ctx.fillStyle = fg;
  const blockH = card.lines.length * LINE_H + GAP + TYPE_PX;
  let ty = cy - blockH / 2 + LINE_H / 2;
  for (const line of card.lines) {
    ctx.fillText(line, cx, ty);
    ty += LINE_H;
  }
  ctx.font = TYPE_FONT;
  ctx.fillStyle = fg === '#FFFFFF' ? 'rgba(255,255,255,0.7)' : 'rgba(10,10,11,0.7)';
  ctx.fillText(opts.type, cx, ty + GAP);
  ctx.restore();
}

/** Render a card to its own canvas (3D sprite texture). */
export function renderCardCanvas(card: CardLayout, opts: DrawOpts, dpr = 2): HTMLCanvasElement {
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(card.w * dpr);
  canvas.height = Math.ceil(card.h * dpr);
  const ctx = canvas.getContext('2d');
  if (ctx) {
    ctx.scale(dpr, dpr);
    drawCardAt(ctx, card.w / 2, card.h / 2, card, opts);
  }
  return canvas;
}
